import moment from 'moment'
import { REQUIREMENT_DATA } from '../requirementData'
import { STATUS_COLORS, STATUS_BACKGROUND_COLORS } from '../theme'
import { STATUS_CATEGORIES } from './constants/statuses'
import { LEVEL, REQUIREMENT, LEVEL_ORDER } from './constants/levels'

export const MEMBER_STATUS_LABEL = {
  [LEVEL.LEVEL_3]: 'Level 3 Associate',
  [LEVEL.LEVEL_2]: 'Level 2 Associate',
  [LEVEL.LEVEL_1]: 'Level 1 Associate',
  QUALIFIER_ELIGIBLE: 'Qualifier Eligible',
  PRE_QUALIFIER: 'Pre-Qualifier',
}

export const getStatusColor = (status) => {
  return STATUS_COLORS[status] || STATUS_COLORS.default
}

export const getStatusBackgroundColor = (status) => {
  return STATUS_BACKGROUND_COLORS[status] || STATUS_BACKGROUND_COLORS.default
}

export const getRequirementDisplayData = (levelKey, requirement) => {
  const levelData = REQUIREMENT_DATA[levelKey]
  if (!levelData) return null

  const reqData = levelData.requirements[requirement.key]
  if (!reqData) return null

  const statusData = reqData.statuses[requirement.status]
  if (!statusData) return null

  const baseStatusData = {
    name: reqData.name,
    color: getStatusColor(requirement.status),
    backgroundColor: getStatusBackgroundColor(requirement.status),
    ...statusData,
  }

  const context = {}

  if (requirement.seminarInfo?.eventStartDate) {
    const { eventStartDate } = requirement.seminarInfo
    const [year, month, day] = eventStartDate.split('-').map(Number)
    const date = new Date(year, month - 1, day)

    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
    
    context.seminarDate = ` that starts on ${formattedDate}`
  } else {
    context.seminarDate = ''
  }

  if (requirement.courseName) {
    context.courseName = ` ${requirement.courseName}`
  } else {
    context.courseName = ''
  }

  const message = interpolate(statusData.message, context)

  return {
    ...baseStatusData,
    message,
  }
}

export const isCompleteStatus = (status) => {
  return STATUS_CATEGORIES.COMPLETE.includes(status)
}

export const isInProgressStatus = (status) => {
  return STATUS_CATEGORIES.IN_PROGRESS.includes(status)
}

export const isLockedStatus = (status) => {
  return STATUS_CATEGORIES.LOCKED.includes(status)
}

export const isOpenStatus = (status) => {
  return STATUS_CATEGORIES.OPEN.includes(status)
}

export const hasAdvancedPastLevel = (levelKey, currentLevelKey, mostRecentCompletedLevelKey = null) => {
  const levelIndex = LEVEL_ORDER.indexOf(levelKey)
  // For users who have completed a level but haven't started the next one - 
  // currentLevelKey is null, fall back to mostRecentCompletedLevelKey
  const effectiveLevelKey = currentLevelKey || mostRecentCompletedLevelKey
  const currentLevelIndex = LEVEL_ORDER.indexOf(effectiveLevelKey)
  return levelIndex < currentLevelIndex
}

export const formatDisplayDate = (dateString) => {
  if (!dateString) return null
  return moment(dateString).format('MM/DD/YYYY')
}


// Calculates program expiration date from Level 1 start date.
// Used as fallback for cross-curriculum users where backend returns null.
export const calculateProgramExpirationDate = (levels, programType = 'Associate') => {
  if (!levels || !Array.isArray(levels)) return null
  
  // Find Level 1 start date (any curriculum version - uses key, not level code)
  const level1 = levels.find(l => l.key === LEVEL.LEVEL_1)
  if (!level1?.startDate) return null
  
  const level1Date = new Date(level1.startDate)
  
  // Duration dates defined in Ccms::Education::PgmProgram model
  let programDurationYears = 9
  
  const STUDENT_CUTOFF = new Date('2025-08-25')
  const ASSOCIATE_CUTOFF = new Date('2026-01-01')
  
  if (programType === 'Student' && level1Date >= STUDENT_CUTOFF) {
    programDurationYears = 7
  } else if (programType === 'Associate' && level1Date >= ASSOCIATE_CUTOFF) {
    programDurationYears = 7
  }
  
  const programExpirationDate = new Date(level1Date)
  programExpirationDate.setFullYear(programExpirationDate.getFullYear() + programDurationYears)
  
  return programExpirationDate.toISOString().split('T')[0]
}

export const formatRemainingTime = (endDate, startDate = moment()) => {
  if (!endDate) return null
  
  const start = moment(startDate)
  const end = moment(endDate)
  
  const totalDays = end.diff(start, 'days')
  
  if (totalDays < 0) return null
  
  if (totalDays < 30) {
    return `${totalDays} Day${totalDays !== 1 ? 's' : ''}`
  }

  const years = end.diff(start, 'years')
  start.add(years, 'years')
  
  const months = end.diff(start, 'months')
  start.add(months, 'months')
  
  const days = end.diff(start, 'days')
  
  if (years === 0) {
    if (months >= 11) {
      return days === 0 ? '11 Months' : `11 Months, ${days} Day${days !== 1 ? 's' : ''}`
    }
    
    if (months === 0) {
      return `${totalDays} Day${totalDays !== 1 ? 's' : ''}`
    }
    
    if (days === 0) {
      return `${months} Month${months !== 1 ? 's' : ''}`
    }
    return `${months} Month${months !== 1 ? 's' : ''}, ${days} Day${days !== 1 ? 's' : ''}`
  }
  
  if (months > 0) {
    if (years === 1) {
      return `1 Year, ${months} Month${months !== 1 ? 's' : ''}`
    } else {
      return `${years} Years, ${months} Month${months !== 1 ? 's' : ''}`
    }
  }
  
  if (days > 0) {
    if (years === 1) {
      return `1 Year, ${days} Day${days !== 1 ? 's' : ''}`
    } else {
      return `${years} Years, ${days} Day${days !== 1 ? 's' : ''}`
    }
  }
  
  return years === 1 ? '1 Year' : `${years} Years`
}

// Replaces {{key}} placeholders with corresponding values from context
// Works for URLs, messages, or any string template
// Examples:
//   interpolate('https://example.com/user/{{pgaId}}/profile', { pgaId: '12345' })
//   => 'https://example.com/user/12345/profile'
//   
//   interpolate('Your seminar is on {{seminarDate}} at {{time}}', { seminarDate: 'Jan 15', time: '8am' })
//   => 'Your seminar is on Jan 15 at 8am'
export const interpolate = (template, context = {}) => {
  if (!template || typeof template !== 'string') return template
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const value = context[key]
    return value !== undefined && value !== null ? String(value) : ''
  })
}

export const getCurrentUnixTimestamp = () => {
  return Math.floor(Date.now() / 1000)
}

export const getMemberStatusLabel = (pgmProgress) => {
  if (!pgmProgress) {
    return null
  }

  const currentLevelKey = pgmProgress.currentLevelKey

  // Levels 1-3: have direct label mappings
  if (MEMBER_STATUS_LABEL[currentLevelKey]) {
    return MEMBER_STATUS_LABEL[currentLevelKey]
  }

  // Qualifying level: determine PQ/QE status based on course manuals completion
  const qualifyingLevel = pgmProgress.levels?.find(l => l.key === LEVEL.QUALIFYING)
  const courseManualsRequirement = qualifyingLevel?.requirements?.find(
    req => req.key === REQUIREMENT.COURSE_MANUALS
  )
  const isCourseManualsComplete = courseManualsRequirement && isCompleteStatus(courseManualsRequirement.status)

  return isCourseManualsComplete
    ? MEMBER_STATUS_LABEL.QUALIFIER_ELIGIBLE
    : MEMBER_STATUS_LABEL.PRE_QUALIFIER
}
